import {
  Autocomplete,
  Box,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactNode, useId } from "react";
import { MediumCustomIcon } from "../../layer00";

export type SettingsFieldOption = {
  value: string;
  label: string;
};

type SettingsFieldProps = {
  label: string;
  icon: ReactNode;
  value: string | null;
  options: SettingsFieldOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

const plainIndicatorSx = {
  border: "none",
  boxShadow: "none",
  borderRadius: ".5rem",
  padding: ".25rem",
};

export const SettingsField = (props: SettingsFieldProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const inputId = useId();

  const selectedOption =
    props.options.find((option) => option.value === props.value) ?? null;

  return (
    <Stack direction="column" rowGap=".5rem">
      <Typography
        component="label"
        htmlFor={inputId}
        variant="cardBodyRegular"
        color={isDark ? theme.palette.text.primary : "#001F4E"}
      >
        {props.label}
      </Typography>
      <Autocomplete
        id={inputId}
        options={props.options}
        value={selectedOption}
        onChange={(_, option) => {
          if (option && option.value !== props.value) {
            props.onChange(option.value);
          }
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disabled={props.disabled}
        disableClearable={!!selectedOption}
        popupIcon={
          <MediumCustomIcon
            icon="CHEVRONDOWN"
            stroke={isDark ? theme.palette.text.secondary : "#989A9C"}
          />
        }
        slotProps={{
          popper: { sx: { zIndex: theme.zIndex.modal + 1 } },
          // Opt out of the bordered MuiIconButton theme style
          popupIndicator: { sx: plainIndicatorSx },
          clearIndicator: { sx: plainIndicatorSx },
        }}
        renderInput={(params) => (
          <Box
            ref={params.InputProps.ref}
            onMouseDown={params.InputProps.onMouseDown}
            className={params.InputProps.className}
            position="relative"
            display="flex"
            flexDirection="row"
            alignItems="center"
            height="2.5rem"
            bgcolor={isDark ? theme.palette.background.default : "#FFFFFF"}
            border={`1px solid ${isDark ? theme.palette.divider : "#CCD2DC"}`}
            borderRadius=".5rem"
            sx={{
              opacity: props.disabled ? 0.6 : 1,
              "& .MuiAutocomplete-endAdornment": {
                right: ".5rem",
                top: "50%",
                transform: "translateY(-50%)",
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              width="2.5rem"
              height="100%"
              borderRight={`1px solid ${
                isDark ? theme.palette.divider : "#E5E8ED"
              }`}
            >
              {props.icon}
            </Box>
            <InputBase
              inputProps={params.inputProps}
              disabled={params.disabled}
              placeholder={props.placeholder}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                paddingLeft: ".75rem",
                paddingRight: "2rem",
                ...theme.typography.formInput,
              }}
            />
            {params.InputProps.endAdornment}
          </Box>
        )}
      />
    </Stack>
  );
};
